import NAVIGATION_MENU, { type NavigationMenuSection } from '@UI/Routing/navigationMenu';
import buildSubjectMenuEntries from '@UI/Routing/buildSubjectMenuEntries';
import useSubjectTypes from '@UI/Hooks/useSubjectTypes';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import * as React from 'react';

export type UseNavigationMenuResult = {
	/** The static catalogue with one entry per subject type, right after "Bénéficiaires". */
	readonly menu: readonly NavigationMenuSection[];
	/** True while the subject types are being fetched. */
	readonly isLoading: boolean;
};

/** Single source of the navigation menu, read by the visible menu and the active-entry lookup. */
const useNavigationMenu = (): UseNavigationMenuResult => {
	const { user } = useAuthenticatedUser();
	const { types, error } = useSubjectTypes();
	const hasAccount = (user?.account?.id ?? null) !== null;

	const menu = React.useMemo(() => {
		if (types === null) {
			return NAVIGATION_MENU;
		}

		const subjectEntries = buildSubjectMenuEntries(types.types);

		return NAVIGATION_MENU.map((section) => {
			const index = section.entries.findIndex((entry) => entry.id === 'beneficiaries');

			if (index === -1) {
				return section;
			}

			return {
				...section,
				entries: [
					...section.entries.slice(0, index + 1),
					...subjectEntries,
					...section.entries.slice(index + 1),
				],
			};
		});
	}, [types]);

	return { menu, isLoading: hasAccount && types === null && error === null };
};

export default useNavigationMenu;
